/**
 * @fileoverview The plants brief for the signed-in gardener's garden, one
 * read shared by every card, tile, row and list that shows a plant's week,
 * planting dates or place in "Needs you first" (api/plantsBrief; D40: the
 * backend decides, the app displays).
 *
 * Kept under [PLANT_OUTLOOK_QUERY_KEY, 'brief', zip, today, language, unit,
 * garden]: under the outlook's own key, so every write that reads the
 * outlook again (a plant added, moved, archived, a bed changed, a stage
 * ticked on Today) reads the brief again too; and with a short signature of
 * the garden's plants (their ids, beds, stages, photos and notes), so a
 * photo or a note moves the order at once, as it did when the app sorted.
 * The last answer stays on screen while the next is on its way. The
 * postcode and the day in the key are the garden place's (useGardenPostcode:
 * the garden's postcode, the owner's in a shared garden, and the garden's own
 * day from its time zone), so the brief is read again at the garden's
 * midnight; no day is sent: the server's brief is for the garden's own day
 * (architecture audit A31), never the phone's.
 *
 * Asked without a postcode too: the planting stages and the order need
 * none (the weeks and dates then come back empty).
 *
 * Where: PlantGrid, PlantCard, PlantRow, PlantTile, TemperatureForecast,
 * PlantingDatesLine, LocationPlantList.
 * API: GET /plants/brief (getPlantsBrief).
 * Settings: OUTLOOK_STALE_TIME_MS, QUERY_RETRY_COUNT (config/tunables/shared).
 *
 * @module usePlantsBrief
 */

import { retryRule } from '@/config/queryRetry';
import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useGardenPostcode } from './useGardenPostcode';
import { usePlantsContext } from './usePlantsContext';
import { useTranslation } from './useTranslation';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { getPlantsBrief, type BriefPlant, type GardenMark, type PlantsBrief } from '@/shared/api/plantsBrief';
import { OUTLOOK_STALE_TIME_MS, PLANT_OUTLOOK_QUERY_KEY, QUERY_RETRY_COUNT } from '@/config/tunables/shared';
import type { Plant } from '@/shared/types/plant';

/** What a reader is handed. */
export interface PlantsBriefReading {
  /** One live plant's entry, or null (archived, not in yet, or no brief). */
  forPlant: (plantObjId: number) => BriefPlant | null;
  /** The garden's marks; empty until the brief is in. */
  marks: GardenMark[];
  /** The "Needs you first" order; empty until the brief is in. */
  order: number[];
  /** Whether the outlook behind it could be read: false until the brief is in, or without one. */
  hasOutlook: boolean;
  /** The brief itself, or undefined until it is in. */
  brief: PlantsBrief | undefined;
  /** True while a read is on its way, a background one included. */
  isFetching: boolean;
}

/**
 * Reads the garden's plants brief again, for a change the brief carries that no plant-list write makes (B110: a Public switch flipped).
 *
 * @param queryClient - The app's query client.
 * @returns Resolves when the brief is read again.
 */
export function refreshPlantsBrief(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: [PLANT_OUTLOOK_QUERY_KEY, 'brief'] });
}

/**
 * A short signature of the garden's plants: whatever the brief is worked out
 * from that the plant list carries (each plant's id, status, bed, stage
 * notes, photos and notes), so a change there reads the brief again.
 *
 * @param plants - The live plants, or none yet.
 * @returns A hash, as text.
 */
function gardenSignature(plants: Plant[] | undefined): string {
  const text = (plants ?? []).map((plant) => [
    plant.plant_obj_id, plant.status, plant.location?.id, plant.location?.sheltered ? 1 : 0,
    plant.stage_marks.map((mark) => `${mark.mark_id}:${mark.stage_id}:${mark.marked_on}`).join(','),
    (plant.photos ?? []).length, (plant.notes ?? []).length, plant.notes?.[0]?.timestamp ?? '',
  ].join('|')).join(';');
  // djb2: short and stable, enough to tell one garden state from the next.
  let hash = 5381;
  for (let i = 0; i < text.length; i++) hash = ((hash * 33) ^ text.charCodeAt(i)) >>> 0;
  return `${(plants ?? []).length}-${hash.toString(36)}`;
}

/**
 * The garden's plants brief.
 *
 * @returns Lookups into it and its state.
 */
export function usePlantsBrief(): PlantsBriefReading {
  const { i18n: { language } } = useTranslation();
  const { mysqlUser } = useAuth();
  const { userConfig } = useUserConfig();
  const { plants } = usePlantsContext();
  // The garden's postcode and its own day, the server's (the owner's in a shared garden).
  const { zip, date: today, known } = useGardenPostcode();
  const unit = userConfig.preferences.temperatureUnit === 'C' ? 'C' : 'F';
  /** The garden's signature, part of the key. */
  const garden = useMemo(() => gardenSignature(plants), [plants]);

  const { data, isFetching } = useQuery({
    queryKey: [PLANT_OUTLOOK_QUERY_KEY, 'brief', zip, today, language, unit, garden],
    queryFn: () => getPlantsBrief(zip, language, unit),
    enabled: Boolean(mysqlUser) && plants !== undefined && known,
    staleTime: OUTLOOK_STALE_TIME_MS,
    retry: retryRule(QUERY_RETRY_COUNT),
    placeholderData: keepPreviousData,
  });

  /**
   * One live plant's entry.
   *
   * @param plantObjId - The plant.
   */
  const forPlant = useCallback((plantObjId: number): BriefPlant | null => data?.plants?.[String(plantObjId)] ?? null, [data]);

  return {
    forPlant,
    marks: data?.marks ?? [],
    order: data?.order ?? [],
    hasOutlook: data?.hasOutlook ?? false,
    brief: data,
    isFetching,
  };
}
